import type { ReactNode, SyntheticEvent } from "react";
import { Link } from "react-router-dom";

import { useUpdateUser } from "../../api/queries";
import { useAuth } from "../../auth/auth-context";
import { Button } from "../../ui/Button";
import { SettingsPage, SettingsSection } from "../../ui/SettingsSection";
import { DeleteAccountSection } from "./DeleteAccountSection";
import { languages } from "./PreferencesScreen";

/**
 * `users.update` with the caller's own id (`docs/spec/users-and-teams.md`
 * § API surface). The field is uncontrolled and keyed on the server's value, so
 * a refreshed profile re-seeds it without an effect.
 */
export function ProfileScreen(): ReactNode {
  const { session, abilitiesFor, refresh } = useAuth();
  const user = session?.info.user;
  const team = session?.info.team;
  const update = useUpdateUser();

  const abilities = abilitiesFor(user?.id);
  // `delete` on a user is "an administrator, or self when the workspace allows
  // it" (`docs/spec/permissions.md` §7.5). The policy is authoritative when the
  // server sent one; otherwise the same rule is applied from what we know.
  const canDeleteAccount =
    abilities.delete ??
    (user?.role === "admin" || (team?.preferences?.membersCanDeleteAccount ?? true));

  const languageLabel =
    languages.find((language) => language.value === user?.language)?.label ??
    user?.language ??
    "English (US)";

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (user === undefined) return;
    const value = new FormData(event.currentTarget).get("name");
    update.mutate(
      { id: user.id, name: typeof value === "string" ? value.trim() : "" },
      {
        onSuccess: () => {
          refresh();
        },
      },
    );
  };

  return (
    <SettingsPage title="Profile" description="How you appear to the rest of the workspace.">
      <SettingsSection title="Your details">
        <form className="flex max-w-lg flex-col gap-3" key={user?.name} onSubmit={submit}>
          <label className="flex flex-col gap-1 text-sm">
            Full name
            <input
              name="name"
              defaultValue={user?.name ?? ""}
              required
              className="rounded-md border border-line bg-raised px-3 py-2 text-sm"
            />
          </label>

          {user?.email !== undefined && (
            <p className="text-sm text-muted">
              Signed in as <span className="font-mono">{user.email}</span>
            </p>
          )}

          <p className="text-sm text-muted">
            Language: {languageLabel}.{" "}
            <Link to="/settings/preferences" className="underline">
              Change it in preferences
            </Link>
            .
          </p>

          {update.isError && (
            <p role="alert" className="text-sm text-danger">
              {update.error.message}
            </p>
          )}
          {update.isSuccess && (
            <p role="status" className="text-sm text-muted">
              Profile saved.
            </p>
          )}

          <div>
            <Button type="submit" variant="primary" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </SettingsSection>

      {canDeleteAccount && <DeleteAccountSection />}
    </SettingsPage>
  );
}
